import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { Stop } from '../../types';
import { serifVoice } from '../../theme';
import { Board, ResearchResult, createStops, ideaToStop, researchTrip } from '../../lib/plan';
import { useElapsed } from './useElapsed';

interface Props {
  open: boolean;
  board: Board;
  onClose: () => void;
  onAdded: (stops: Stop[]) => void;
  /** Opens with this focus filled in, e.g. a layover. */
  initialFocus?: string;
}

/**
 * Step 3: research. Roamoir searches for places that fit the trip's themes; the
 * traveller keeps what they want and the rest is never saved. Nothing lands in
 * the trip without being looked at first.
 */
export default function ResearchDialog({ open, board, onClose, onAdded, initialFocus = '' }: Props) {
  const [focus, setFocus] = useState(initialFocus);
  useEffect(() => {
    if (open) setFocus(initialFocus);
  }, [open, initialFocus]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ResearchResult | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const elapsed = useElapsed(running);

  function reset() {
    setResult(null);
    setPicked(new Set());
    setError(null);
  }

  async function run() {
    reset();
    setRunning(true);
    const { data, error: err } = await researchTrip(board.trip.trip_id, focus.trim());
    setRunning(false);
    if (err || !data) {
      setError(err ?? 'No result');
      return;
    }
    setResult(data);
    setPicked(new Set(data.ideas.map((_, i) => i)));
  }

  async function addPicked() {
    if (!result) return;
    setSaving(true);
    const chosen = result.ideas.filter((_, i) => picked.has(i)).map(ideaToStop);
    const { data, error: err } = await createStops(board.trip.trip_id, chosen);
    setSaving(false);
    if (err) {
      setError(err);
      return;
    }
    onAdded(data);
    reset();
    setFocus('');
    onClose();
  }

  function toggle(i: number) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  const themes = board.trip.themes.length ? board.trip.themes.join(', ') : 'a balanced mix';

  return (
    <Dialog open={open} onClose={running || saving ? undefined : onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>Research {board.trip.destination}</DialogTitle>
      <DialogContent dividers>
        {!result && !running ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Roamoir looks for must-sees and interests that fit {themes}, checked against what is open on
              your dates. You choose which become candidates.
            </Typography>
            <TextField
              label="Focus this pass (optional)"
              placeholder="Etruscan sites · day hikes from Siena · somewhere for a long lunch"
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              fullWidth
              multiline
              maxRows={3}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Run it again with a different focus to go deeper on one thing. Places you already have are skipped.
            </Typography>
          </Box>
        ) : null}

        {running ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Researching… {elapsed}s
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Usually under a minute.
            </Typography>
          </Box>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}

        {result ? (
          <Box>
            {result.notes ? (
              <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                <Typography variant="body2" sx={serifVoice}>
                  {result.notes}
                </Typography>
              </Alert>
            ) : null}
            {result.ideas.length === 0 ? (
              <Typography color="text.secondary">Nothing new turned up. Try a different focus.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {result.ideas.map((idea, i) => (
                  <Box
                    key={`${idea.title}-${i}`}
                    onClick={() => toggle(i)}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      p: 1.5,
                      border: '1px solid',
                      borderColor: picked.has(i) ? 'primary.light' : 'divider',
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      opacity: picked.has(i) ? 1 : 0.6,
                    }}
                  >
                    <Checkbox checked={picked.has(i)} size="small" sx={{ p: 0.5, alignSelf: 'flex-start' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 600 }}>
                          {idea.icon} {idea.title}
                        </Typography>
                        {idea.city ? (
                          <Typography variant="body2" color="text.secondary">
                            {idea.city}
                          </Typography>
                        ) : null}
                        {idea.must_see ? (
                          <Chip
                            icon={<StarIcon sx={{ fontSize: 14 }} />}
                            label="must-see"
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        ) : null}
                        {idea.hours ? (
                          <Typography variant="caption" color="text.secondary">
                            ~{idea.hours} h
                          </Typography>
                        ) : null}
                      </Box>
                      {idea.why ? (
                        <Typography variant="body2" sx={{ ...serifVoice, mt: 0.5 }}>
                          {idea.why}
                        </Typography>
                      ) : null}
                      {idea.booking ? (
                        <Typography variant="caption" color="secondary.main" sx={{ display: 'block', mt: 0.5 }}>
                          Book ahead: {idea.booking}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        {result ? (
          <>
            <Button onClick={reset} disabled={saving}>
              Search again
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={onClose} disabled={saving}>
              Discard
            </Button>
            <Button variant="contained" onClick={addPicked} disabled={saving || picked.size === 0}>
              {saving ? <CircularProgress size={20} color="inherit" /> : `Add ${picked.size} to candidates`}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={running}>
              Cancel
            </Button>
            <Button variant="contained" onClick={run} disabled={running}>
              Research
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
